import api from './api';

// ========================================
// TYPES
// ========================================

export interface LabPanel {
  id: string;
  panelId?: string;
  clinic_id?: string | null;
  name: string;
  description?: string;
  treatment_area?: 'HRT_MEN' | 'HRT_WOMEN' | 'WEIGHT_LOSS' | 'HAIR_LOSS' | 'GENERAL' | 'NIMCORE_MEN' | 'NIMCORE_WOMEN';
  treatmentArea?: 'HRT_MEN' | 'HRT_WOMEN' | 'WEIGHT_LOSS' | 'HAIR_LOSS' | 'GENERAL' | 'NIMCORE_MEN' | 'NIMCORE_WOMEN' | string;
  is_default?: boolean;
  isDefault?: boolean;
  status?: 'active' | 'inactive';
  display_order?: number;
  displayOrder?: number;
  price?: number;
  patientPrice?: number;
  junctionTestId?: string;
  collectionMethod?: string;
  collection_method?: string;
  labSlug?: string;
  fastingRequired?: boolean;
  fasting_required?: boolean;
  sampleType?: string;
  turnaroundDays?: number;
  metadata?: Record<string, unknown>;
  internalTests?: LabInternalTest[];
  tests?: Array<{
    id: string;
    name: string;
    description?: string;
    category?: string;
  }>;
  markers?: Array<{
    id?: number;
    name: string;
    slug?: string;
    type?: string;
    loincCode?: string;
    unit?: string;
  }>;
  method?: string;
  category?: string;
  isActive?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface LabInternalTest {
  id: string;
  name: string;
  description?: string;
  category: string;
  loinc_code?: string;
  vital_test_id?: string;
  expected_markers?: ExpectedMarker[];
  sample_type?: string;
  fasting_required?: boolean;
  turnaround_days?: number;
  status: 'active' | 'inactive';
}

export interface ProviderTest {
  id: string;
  name: string;
  description?: string;
  method: string;
  price?: number;
  markers: Array<{
    name: string;
    loincCode?: string;
  }>;
  sampleType?: string;
  category?: string;
  isActive: boolean;
  lab?: string;
}

export interface ExpectedMarker {
  loincCode: string;
  loincDisplayName: string;
  unit: string;
  normalRanges: NormalRange[];
}

export interface NormalRange {
  min: number;
  max: number;
  unit: string;
  gender: 'male' | 'female' | 'all';
}

export interface LabCenter {
  centerId: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  phone?: string;
  hours?: string;
  distance?: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface TimeSlot {
  slotId: string;
  startTime: string;
  endTime: string;
  available: boolean;
  bookingKey?: string;
  expiresAt?: string;
  metadata?: {
    price?: number;
    isPriority?: boolean;
    numAppointmentsAvailable?: number;
    timezone?: string;
    date?: string;
  };
}

export interface LabOrder {
  id: string;
  patient_id: string;
  clinic_id: string;
  panel_id: string;
  vital_user_id?: string;
  vital_order_id?: string;
  provider_metadata?: {
    lab_test?: {
      name?: string;
      lab?: {
        name?: string;
        first_line_address?: string;
        second_line_address?: string;
        city?: string;
        state?: string;
        zipcode?: string;
        zip?: string;
      };
    };
    lab_tests?: Array<{ name?: string; display_name?: string }>;
  };
  provider_test_ids?: string[];
  panel_name?: string;
  status: LabOrderStatus;
  collection_method: 'walk_in' | 'at_home_phlebotomy';
  patient_details: PatientDetails;
  patient_address: PatientAddress;
  appointment_details?: AppointmentDetails;
  lab_center_id?: string;
  scheduled_at?: string;
  requisition_url?: string;
  // Backend stores this as DECIMAL(10,2); Sequelize returns decimals as
  // *strings* to preserve precision. Typed as `number | string` so
  // TypeScript forces callers to coerce via `Number(…)` before doing
  // numeric ops — avoids runtime crashes like `.toFixed is not a function`.
  price_charged?: number | string;
  payment_status?: 'pending' | 'paid' | 'refunded' | 'waived';
  cancelled_at?: string;
  cancellation_reason?: string;
  created_at?: string;
  updated_at?: string;
  guided_order_id?: string | null;
  guidedOrder?: { id: string };
  // Encounter linkage (nullable for off-visit orders)
  encounter_id?: string | null;
  internal_appointment_id?: string | null;
  off_encounter_reason?: string | null;
  junction_appointment_id?: string | null;
  // Associations
  panel?: LabPanel;
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    email?: string;
    phone?: string;
  };
  results?: LabResult[];
}

export type LabOrderStatus =
  // Pre-provider (payment gate)
  | 'payment_pending'              // Order created; awaiting patient payment
  | 'payment_failed'               // PaymentIntent declined; retryable via pay link
  | 'provider_submission_failed'   // Paid but Junction submit exhausted retries
  // Junction lifecycle
  | 'order_created'
  | 'pending'
  | 'requisition_ready'
  | 'appointment_pending'
  | 'pending_appointment'
  | 'appointment_scheduled'
  | 'appointment_cancelled'
  | 'scheduled'
  | 'draw_completed'
  | 'sample_collected'
  | 'sample_with_lab'
  | 'partial_results'
  | 'processing'
  | 'completed'
  | 'cancelled'
  | 'failed'
  | 'sample_error';

export interface PatientDetails {
  firstName: string;
  lastName: string;
  dob: string;
  gender: 'male' | 'female' | 'unknown';
  phone: string;
  email: string;
}

export interface PatientAddress {
  receiverName?: string;
  street?: string;
  firstLine: string;
  secondLine?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  phoneNumber?: string;
}

export interface AppointmentDetails {
  bookingKey?: string;
  appointmentId?: string;
  scheduledAt?: string;
  date?: string;
  time?: string;
  location?:
    | string
    | {
        name?: string;
        address?: string | Record<string, unknown>;
        lat?: number;
        lng?: number;
      };
}

export interface LabResult {
  id: string;
  order_id: string;
  patient_id: string;
  status: 'preliminary' | 'final' | 'amended' | 'corrected';
  observations: ResultObservation[];
  custom_report_url?: string;
  provider_report_url?: string;
  collection_date?: string;
  received_at?: string;
  reviewed_at?: string;
  reviewed_by?: string;
  has_critical_values?: boolean;
  patient_notified?: boolean;
  created_at?: string;
  updated_at?: string;
  // Associations
  order?: LabOrder;
}

export interface ResultObservation {
  // Primary display fields (from database)
  loincDisplayName?: string;
  providerMarkerName?: string;
  // Legacy field names
  markerName?: string;
  marker_name?: string;
  name?: string;
  test_name?: string;
  // Value fields
  value: string | number;
  result?: string | number;
  resultValue?: string;
  // Result type from Vital API
  type?: 'numeric' | 'range' | 'comment'; // Type of result
  // Other fields
  unit?: string;
  units?: string;
  referenceRange?: string;
  reference_range?: string;
  interpretation: 'N' | 'H' | 'L' | 'C' | 'normal' | 'abnormal'; // Normal, High, Low, Critical
  abnormalFlag?: string;
  loincCode?: string;
  status?: string;
}

// ========================================
// CREATE ORDER REQUEST
// ========================================

export interface CreateLabOrderRequest {
  patientId: string;
  clinicId?: string; // Required by the backend; pulled from header or body
  panelId?: string; // Legacy: panel ID (for backward compatibility)
  testIds?: string[]; // New: direct provider test IDs
  collectionMethod: 'walk_in' | 'at_home_phlebotomy';
  patientDetails: PatientDetails;
  patientAddress: PatientAddress;
  labCenterId?: string;
  notifyPatient?: boolean; // Auto-send notification to patient (default: true)
  notes?: string;
  guidedOrderId?: string; // Link this lab order to a guided order
  // Encounter linkage — required by requireClinicalContext middleware.
  // In-visit orders supply encounter_id; off-visit (storefront, external)
  // supply off_encounter_reason.
  encounter_id?: string;
  appointment_id?: string;
  off_encounter_reason?:
    | 'refill_request'
    | 'chart_correction'
    | 'letter'
    | 'external_import'
    | 'pre_visit_task'
    | 'external_order'
    | 'pre_visit_lab'
    | 'direct_rx'
    | 'patient_storefront';
  // Pay-at-creation: when skipPayment=true the order is created unpaid
  // and the patient receives an email pay link. Otherwise paymentMethodId
  // must be provided to charge synchronously against the clinic's
  // connected Stripe account.
  skipPayment?: boolean;
  paymentMethodId?: string;
  stripeCustomerId?: string;
}

// ========================================
// INTELLIHEALTH LAB ORDER TYPES
// ========================================

export interface TestMatchResult {
  matched: Array<{ testName: string; providerTestId: string }>;
  unmatched: Array<{ testName: string; code?: string }>;
}

export interface TherapyLabPanel {
  name: string;
  panelIds: string[];
  price: number;
  fastingRequired: boolean;
  markerCount: number;
  collectionMethods: string[];
}

export interface IntelliHealthLabOrderRequest {
  patientId: string;
  testIds?: string[];
  panelId?: string;
  collectionMethod: 'walk_in' | 'at_home_phlebotomy';
  patientDetails: PatientDetails;
  patientAddress: PatientAddress;
  fasting: boolean;
  notes?: string;
  notifyPatient?: boolean;
}

// ========================================
// API METHODS
// ========================================

// Panels
export const getPanels = async (treatmentArea?: string): Promise<LabPanel[]> => {
  const params = treatmentArea ? { treatmentArea } : {};
  const response = await api.get('/labs/panels', { params });
  return response.data.panels;
};

export const getPanelById = async (panelId: string): Promise<LabPanel> => {
  const response = await api.get(`/labs/panels/${panelId}`);
  return response.data.panel;
};

// Provider Tests
export const getAvailableTests = async (params?: {
  provider?: string;
  search?: string;
  method?: string;
  refresh?: boolean;
}): Promise<ProviderTest[]> => {
  const response = await api.get('/labs/tests', { params });
  return response.data.tests || [];
};

/**
 * Get panels from Nimbus Lab Service
 * Fetches lab panels grouped by treatment area
 */
export const getPanelsFromLabService = async (treatmentArea?: string): Promise<LabPanel[]> => {
  const params: Record<string, string> = {
    type: 'panels',
    provider: 'nimbus',
  };
  if (treatmentArea) {
    params.treatmentArea = treatmentArea;
  }
  const response = await api.get('/labs/tests', { params });
  return response.data.tests || [];
};

export const getTestDetails = async (testId: string, provider?: string): Promise<ProviderTest> => {
  const params = provider ? { provider } : {};
  const response = await api.get(`/labs/tests/${testId}`, { params });
  return response.data.test;
};

// Lab Centers
export const searchLabCenters = async (zipCode: string, radius?: number): Promise<LabCenter[]> => {
  const params: Record<string, string | number> = { zipCode };
  if (radius) params.radius = radius;
  const response = await api.get('/labs/centers', { params });
  return response.data.centers;
};

// Availability
export const checkPhlebotomyAvailability = async (
  address: PatientAddress,
  startDate?: string,
  endDate?: string
): Promise<TimeSlot[]> => {
  const response = await api.post('/labs/availability', {
    address,
    startDate,
    endDate,
  });
  return response.data.slots;
};

export const getPSCAvailability = async (
  zipCode: string,
  radius?: string,
  startDate?: string,
  siteCodes?: string[]
): Promise<unknown> => {
  const params: Record<string, string> = { zipCode };
  if (radius) params.radius = radius;
  if (startDate) params.startDate = startDate;
  if (siteCodes) params.siteCodes = siteCodes.join(',');
  const response = await api.get('/labs/psc-availability', { params });
  return response.data.availability;
};

// Orders
export const createOrder = async (orderData: CreateLabOrderRequest): Promise<LabOrder> => {
  // The backend's requireClinicalContext middleware demands an encounter_id
  // or an explicit off_encounter_reason. Current lab-order entry points are
  // all out-of-visit; default to 'external_order' so callers stay concise.
  // The Start Visit workflow (Phase 2) will supply encounter_id instead.
  const enriched: CreateLabOrderRequest = {
    ...orderData,
    off_encounter_reason:
      orderData.off_encounter_reason
      || (orderData.encounter_id ? undefined : 'external_order'),
  };
  const response = await api.post('/labs/orders', enriched);
  return response.data.order;
};

// ========================================
// INTELLIHEALTH LAB ORDER INTEGRATION
// ========================================

export const matchTestsFromCatalog = (
  tests: Array<{ name: string; code?: string; loincCode?: string }>,
  catalog: ProviderTest[]
): TestMatchResult => {
  const matched: TestMatchResult['matched'] = []
  const unmatched: TestMatchResult['unmatched'] = []

  for (const test of tests) {
    let found: ProviderTest | undefined

    // Priority 1: exact LOINC match against provider test markers
    if (test.loincCode) {
      found = catalog.find(pt =>
        pt.markers?.some(m => m.loincCode === test.loincCode)
      )
    }

    // Priority 2: case-insensitive name substring match
    if (!found) {
      const needle = test.name.toLowerCase()
      found = catalog.find(pt => {
        const haystack = pt.name.toLowerCase()
        return haystack.includes(needle) || needle.includes(haystack)
      })
    }

    if (found) {
      matched.push({ testName: test.name, providerTestId: found.id })
    } else {
      unmatched.push({ testName: test.name, code: test.code })
    }
  }

  return { matched, unmatched }
}

export const createOrderFromIntelliHealth = async (
  request: IntelliHealthLabOrderRequest
): Promise<LabOrder> => {
  if (!request.panelId && (!request.testIds || !request.testIds.length)) {
    throw new Error('No panel or matching tests specified for this lab order.')
  }

  const orderData: CreateLabOrderRequest = {
    patientId: request.patientId,
    collectionMethod: request.collectionMethod,
    patientDetails: request.patientDetails,
    patientAddress: request.patientAddress,
    notifyPatient: request.notifyPatient ?? true,
  }

  if (request.panelId) {
    orderData.testIds = [request.panelId]
  } else if (request.testIds) {
    orderData.testIds = request.testIds
  }

  if (request.notes) {
    orderData.notes = request.notes
  }

  return createOrder(orderData)
}

export const getPatientOrders = async (patientId: string, status?: string): Promise<LabOrder[]> => {
  const params = status ? { status } : {};
  const response = await api.get(`/labs/patients/${patientId}/orders`, { params });
  return response.data.orders;
};

/**
 * Fetch lab orders linked to a specific encounter, with their
 * standardized (LOINC-coded) results inlined.
 *
 * Backed by the cockpit-only endpoint
 *   GET /api/emr/:clinicId/encounters/:encounterId/lab-orders
 *
 * Use this from inside the visit cockpit instead of `getPatientOrders`
 * + client-side filter — that pattern fetches the patient's full lab
 * history just to throw most of it away.
 */
export const getEncounterLabOrders = async (
  clinicId: string,
  encounterId: string,
): Promise<LabOrder[]> => {
  const response = await api.get(
    `/emr/${clinicId}/encounters/${encounterId}/lab-orders`,
  );
  return response.data.orders || [];
};

/**
 * Fetch lab orders linked to a specific appointment. Used by the
 * pre-visit AppointmentSheet panel (where the encounter doesn't
 * exist yet). Once the encounter is opened, these orders auto-link
 * to it and become available via `getEncounterLabOrders`.
 */
export const getAppointmentLabOrders = async (
  clinicId: string,
  appointmentId: string,
): Promise<LabOrder[]> => {
  const response = await api.get(
    `/emr/${clinicId}/appointments/${appointmentId}/lab-orders`,
  );
  return response.data.orders || [];
};

export const getOrderById = async (orderId: string): Promise<LabOrder> => {
  const response = await api.get(`/labs/orders/${orderId}`);
  return response.data.order;
};

export const bookAppointment = async (
  orderId: string,
  bookingKey: string,
  type?: 'psc' | 'phlebotomy'
): Promise<LabOrder> => {
  const response = await api.post(`/labs/orders/${orderId}/appointment`, {
    bookingKey,
    type,
  });
  return response.data.order;
};

export const cancelOrder = async (orderId: string, reason?: string): Promise<LabOrder> => {
  const response = await api.post(`/labs/orders/${orderId}/cancel`, { reason });
  return response.data.order;
};

export const getRequisitionPDF = async (orderId: string): Promise<Blob> => {
  const response = await api.get(`/labs/orders/${orderId}/requisition`, {
    responseType: 'blob',
  });
  return response.data;
};

// Results
export const getPatientResults = async (patientId: string): Promise<LabResult[]> => {
  const response = await api.get(`/labs/patients/${patientId}/results`);
  return response.data.results;
};

export const getResultById = async (resultId: string): Promise<LabResult> => {
  const response = await api.get(`/labs/results/${resultId}`);
  return response.data.result;
};

export const getResultPDF = async (resultId: string): Promise<Blob> => {
  const response = await api.get(`/labs/results/${resultId}/pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

// Admin
export const getClinicOrders = async (
  clinicId: string,
  options?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }
): Promise<{ orders: LabOrder[]; total: number }> => {
  const response = await api.get(`/clinic/${clinicId}/labs/orders`, { params: options });
  return { orders: response.data.orders, total: response.data.pagination?.total || 0 };
};

export const getUnreviewedResults = async (): Promise<LabResult[]> => {
  const response = await api.get('/labs/admin/results/unreviewed');
  return response.data.results;
};

export const markResultReviewed = async (resultId: string, notes?: string): Promise<LabResult> => {
  const response = await api.post(`/labs/admin/results/${resultId}/review`, { notes });
  return response.data.result;
};

export const getVitalTests = async (): Promise<unknown[]> => {
  const response = await api.get('/labs/admin/vital-tests');
  return response.data.tests;
};

// ========================================
// UTILITY FUNCTIONS
// ========================================

export const getOrderStatusLabel = (status: LabOrderStatus): string => {
  const labels: Record<LabOrderStatus, string> = {
    // Pre-provider
    payment_pending: 'Awaiting Payment',
    payment_failed: 'Payment Failed',
    provider_submission_failed: 'Lab Submission Failed',
    // Junction lifecycle
    order_created: 'Order Created',
    pending: 'Pending',
    requisition_ready: 'Requisition Ready',
    appointment_pending: 'Scheduling Appointment',
    pending_appointment: 'Needs Appointment',
    appointment_scheduled: 'Appointment Scheduled',
    appointment_cancelled: 'Appointment Cancelled',
    scheduled: 'Scheduled',
    draw_completed: 'Draw Completed',
    sample_collected: 'Sample Collected',
    sample_with_lab: 'Sample at Lab',
    partial_results: 'Partial Results',
    processing: 'Processing',
    completed: 'Completed',
    cancelled: 'Cancelled',
    failed: 'Failed',
    sample_error: 'Sample Error',
  };
  return labels[status] || status;
};

export const getOrderStatusColor = (status: LabOrderStatus): string => {
  const colors: Record<LabOrderStatus, string> = {
    // Pre-provider — amber/red mirror the "money attention" severity
    payment_pending: 'bg-amber-100 text-amber-800 border-amber-200',
    payment_failed: 'bg-red-100 text-red-800 border-red-200',
    provider_submission_failed: 'bg-red-200 text-red-900 border-red-300',
    // Junction lifecycle
    order_created: 'bg-slate-100 text-slate-800',
    pending: 'bg-yellow-100 text-yellow-800',
    requisition_ready: 'bg-blue-100 text-blue-800',
    appointment_pending: 'bg-amber-100 text-amber-800',
    pending_appointment: 'bg-orange-100 text-orange-800',
    appointment_scheduled: 'bg-purple-100 text-purple-800',
    appointment_cancelled: 'bg-gray-200 text-gray-700',
    scheduled: 'bg-purple-100 text-purple-800',
    draw_completed: 'bg-teal-100 text-teal-800',
    sample_collected: 'bg-indigo-100 text-indigo-800',
    sample_with_lab: 'bg-cyan-100 text-cyan-800',
    partial_results: 'bg-lime-100 text-lime-800',
    processing: 'bg-cyan-100 text-cyan-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-800',
    failed: 'bg-red-100 text-red-800',
    sample_error: 'bg-red-200 text-red-900',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Normalize interpretation / resultLevel values from different lab sources
 * (e.g. ZRT uses "Ok"/"H"/"L"/"C", others use "N"/"H"/"L"/"C" or long-form).
 */
export const normalizeInterpretation = (raw: string): string => {
  const lower = raw.trim().toLowerCase();
  if (lower === 'n' || lower === 'normal' || lower === 'ok') return 'N';
  if (lower === 'h' || lower === 'high' || lower === 'elevated') return 'H';
  if (lower === 'l' || lower === 'low') return 'L';
  if (lower === 'c' || lower === 'critical') return 'C';
  return raw;
};

export const getInterpretationLabel = (interpretation: string): string => {
  const labels: Record<string, string> = {
    N: 'Normal',
    H: 'High',
    L: 'Low',
    C: 'Critical',
  };
  return labels[interpretation] || interpretation;
};

export const getInterpretationColor = (interpretation: string): string => {
  const colors: Record<string, string> = {
    N: 'text-green-600',
    H: 'text-red-600',
    L: 'text-blue-600',
    C: 'text-red-800 font-bold',
  };
  return colors[interpretation] || 'text-gray-600';
};

export const getTreatmentAreaLabel = (area: string): string => {
  const labels: Record<string, string> = {
    HRT_MEN: "Men's Health",
    HRT_WOMEN: "Women's Health",
    WEIGHT_LOSS: 'Weight Loss',
    HAIR_LOSS: 'Hair Loss',
    GENERAL: 'General',
  };
  return labels[area] || area;
};
